import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useLocaleByLanguage from "../../../../../../utils/hooks/useLocaleByLanguage";
import { Exercise } from "../../../../../models/Exercise";
import { SwimmingLesson } from "../../../../../models/SwimmingScheduleData";
import { Ticket } from "../../../../../models/Ticket";
import { State } from "../../../../../store";
import useMethodAccount from "../../../../MyAccount/hooks/useMethodAccount";
import { getSwimmingSchoolAbsenceLimit } from "../SwimmingSchool/getSwimmingSchoolAbsenceLimit";
import formatSwimmingTermLabel, {
  formatSwimmingTermDayLabel,
  formatTimeRange,
} from "./ChangeSwimmingTerm/formatSwimmingTermLabel";
import useChangeSwimmingTerm from "./ChangeSwimmingTerm/useChangeSwimmingTerm";

export type ReservationModalView = "details" | "resign" | "change-slots" | "change-swap";

interface UseReservationsModalParams {
  isOpen: boolean;
  onClose: () => void;
  exercise: Exercise;
  ticket: Ticket;
  onRefreshReservations?: () => void;
}

export default function useReservationsModal({
  isOpen,
  onClose,
  exercise,
  ticket,
  onRefreshReservations,
}: UseReservationsModalParams) {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const [view, setView] = useState<ReservationModalView>("details");
  const [now, setNow] = useState(Date.now());
  const [isResignSubmitting, setIsResignSubmitting] = useState(false);
  const [resignError, setResignError] = useState("");
  const [changeError, setChangeError] = useState("");
  const [selectedLesson, setSelectedLesson] = useState<SwimmingLesson | null>(null);
  const { resignExercise } = useMethodAccount();
  const { changeTerm, isSubmitting: isChangeSubmitting } = useChangeSwimmingTerm();

  const allowToChangeDate = useSelector((state: State) => state.config.configuration.allowReservedExerciseChange);
  const blockTimeBeforeExercise = useSelector(
    (state: State) => state.config.configuration.blockTimeBeforeExerciseDateChange,
  );

  useEffect(() => {
    if (isOpen) {
      setNow(Date.now());
      setView("details");
      setResignError("");
      setChangeError("");
      setSelectedLesson(null);
    }
  }, [isOpen]);

  const isAttended = exercise.status === "U" || exercise.status === "Y";
  const isEventPassed = isAttended || new Date(exercise.startTime) <= new Date();

  const canShowResignButton = (): boolean => {
    if (!allowToChangeDate && !ticket.isExerciseSelfService) {
      return false;
    }
    return true;
  };

  const isResignButtonDisabled = (): { disabled: boolean; tooltip?: string } => {
    const exerciseTime = exercise.startTime < 1e11 ? exercise.startTime * 1000 : exercise.startTime;

    if (exerciseTime <= now) {
      return {
        disabled: true,
        tooltip:
          t("rezygnacja_niedostepna_rozpoczete_zajecia") ||
          "Rezygnacja jest niedostępna — wydarzenie już się rozpoczęło.",
      };
    }

    if (now >= exerciseTime - blockTimeBeforeExercise * 60_000) {
      return {
        disabled: true,
        tooltip:
          t("nie_mozna_zrezygnowac_minal_czas") ||
          "Nie możesz już zrezygnować — minął czas przewidziany na zmianę rezerwacji.",
      };
    }

    return { disabled: false };
  };

  const resetChangeFlow = () => {
    setChangeError("");
    setSelectedLesson(null);
    setView("details");
  };

  const handleClose = () => {
    resetChangeFlow();
    setResignError("");
    onRefreshReservations?.();
    onClose();
  };

  const handleResignClick = () => {
    setView("resign");
  };

  const handleCancelResignation = () => {
    setView("details");
  };

  const handleConfirmResignation = async () => {
    if (isResignSubmitting) {
      return;
    }
    setIsResignSubmitting(true);
    setView("details");
    setResignError("");
    try {
      const result = await resignExercise(exercise, () => {
        onRefreshReservations?.();
        onClose();
      });
      if (result && !result.ok) {
        setResignError(result.error);
      }
    } finally {
      setIsResignSubmitting(false);
    }
  };

  // Krok "czy na pewno chcesz zmienić termin" (dawne view "change-confirm") zostaje pominięty
  // — od razu pokazujemy listę terminów. Pytanie "czy na pewno" ma sens dopiero przy realnym
  // wyborze konkretnego nowego terminu (change-swap), nie wcześniej.
  const handleStartChangeTerm = () => {
    setChangeError("");
    setView("change-slots");
  };

  const handleGoToSlots = () => {
    setView("change-slots");
  };

  const handleSelectLesson = (lesson: SwimmingLesson) => {
    setSelectedLesson(lesson);
    setChangeError("");
    setView("change-swap");
  };

  const handleCancelSwap = () => {
    setChangeError("");
    setView("change-slots");
  };

  const handleConfirmSwap = async () => {
    if (!selectedLesson || isChangeSubmitting) {
      return;
    }
    setChangeError("");
    const result = await changeTerm(
      exercise,
      selectedLesson,
      ticket,
      t("termin_szkolki_zmieniony") || t("termin_zostal_pomyslnie_wybrany"),
    );
    if (!result.ok) {
      setChangeError(result.error);
      if (result.stage === "join") {
        setView("change-slots");
      }
      return;
    }
    onRefreshReservations?.();
    onClose();
  };

  const handleBack = () => {
    if (view === "change-slots") {
      resetChangeFlow();
      return;
    }
    if (view === "change-swap") {
      setChangeError("");
      setView("change-slots");
    }
  };

  const resignButtonState = isResignButtonDisabled();
  const showResignButton = canShowResignButton() && !isEventPassed;
  const absenceLimit = getSwimmingSchoolAbsenceLimit(ticket);
  const showAbsenceLimitChip = Boolean(absenceLimit) && showResignButton;

  const tooltipMessage =
    resignButtonState.tooltip ||
    absenceLimit?.tooltip ||
    t("mozesz_zrezygnowac_z_tej_rezerwacji") ||
    "Możesz zrezygnować z tej rezerwacji";

  const isChangeFlow = view === "change-slots" || view === "change-swap";

  const stepLabel = view === "change-slots" ? "Krok 1 z 2" : view === "change-swap" ? "Krok 2 z 2" : "";
  const modalTitle =
    view === "change-slots"
      ? "Wybierz nowy termin"
      : view === "change-swap"
        ? "Potwierdź nowy termin"
        : t("szczegoly_rezerwacji");
  const modalSubtitle = stepLabel && exercise.name ? `${stepLabel} · ${exercise.name}` : stepLabel;

  // Do dawnego (nieużywanego już w UI) pełnego zdania — zostaje na wypadek gdyby coś jeszcze z tego korzystało.
  const fromLabel = formatSwimmingTermLabel(exercise.startTime, locale);
  const toLabel = selectedLesson ? formatSwimmingTermLabel(selectedLesson.startTime, locale) : "";

  // Dane do karty "TWÓJ OBECNY TERMIN" (krok 1) i porównania "BYŁO/BĘDZIE" (krok 2).
  const currentTermDayLabel = formatSwimmingTermDayLabel(exercise.startTime, locale);
  const currentTermTimeLabel = formatTimeRange(exercise.startTime, exercise.closeTime);
  const newTermDayLabel = selectedLesson ? formatSwimmingTermDayLabel(selectedLesson.startTime, locale) : "";
  const newTermTimeLabel = selectedLesson ? formatTimeRange(selectedLesson.startTime, selectedLesson.closeTime) : "";

  const newTermRemainingSpots = selectedLesson
    ? Math.max(0, selectedLesson.capacity - (selectedLesson.reserved + (selectedLesson.prereservation ?? 0)))
    : 0;

  const changeDeadlineHours = Math.max(0, Math.round((blockTimeBeforeExercise || 0) / 60));

  return {
    view,
    modalTitle,
    modalSubtitle,
    isChangeFlow,
    resignError,
    changeError,
    selectedLesson,
    isResignSubmitting,
    isChangeSubmitting,
    resignButtonState,
    showResignButton,
    tooltipMessage,
    absenceLimit,
    showAbsenceLimitChip,
    fromLabel,
    toLabel,
    currentTermDayLabel,
    currentTermTimeLabel,
    newTermDayLabel,
    newTermTimeLabel,
    newTermRemainingSpots,
    changeDeadlineHours,
    exerciseLocation: exercise.location,
    handleClose,
    handleBack,
    handleResignClick,
    handleCancelResignation,
    handleConfirmResignation,
    handleStartChangeTerm,
    handleGoToSlots,
    handleSelectLesson,
    handleCancelSwap,
    handleConfirmSwap,
    resetChangeFlow,
  };
}
